import React, { useState } from 'react';
import { Calendar, RefreshCw, Tag, ToggleRight } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useCreateAction } from '../../hooks/useScheduledActions';
import { useInvoiceTemplatesByService } from '../../hooks/useInvoiceTemplates';
import { useReadingTemplatesByService } from '../../hooks/useReadingTemplates';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';


const emptyForm = () => ({
    type: 'create_invoice',
    frequency: 'monthly',
    nextRunAt: '',
    isActive: true,
    templateUid: '',
});

const AddActionModal = ({ isOpen, onClose, serviceUid }) => {
    const [formData, setFormData] = useState(emptyForm());
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const createAction = useCreateAction();
    const { data: invoiceTemplates = [], isLoading: loadingInvoiceTemplates } = useInvoiceTemplatesByService(serviceUid);
    const { data: readingTemplates = [], isLoading: loadingReadingTemplates } = useReadingTemplatesByService(serviceUid);
    const { t } = useLanguage();

    const typeOptions = [
        { value: 'create_invoice', label: t(KEYS.SERVICE_ACTIONS_TYPE_CREATE_INVOICE) },
        { value: 'create_reading', label: t(KEYS.SERVICE_ACTIONS_TYPE_CREATE_READING) },
    ];

    const frequencyOptions = [
        { value: 'once', label: t(KEYS.SERVICE_ACTIONS_FREQ_ONCE) },
        { value: 'daily', label: t(KEYS.SERVICE_ACTIONS_FREQ_DAILY) },
        { value: 'weekly', label: t(KEYS.SERVICE_ACTIONS_FREQ_WEEKLY) },
        { value: 'monthly', label: t(KEYS.SERVICE_ACTIONS_FREQ_MONTHLY) },
        { value: 'yearly', label: t(KEYS.SERVICE_ACTIONS_FREQ_YEARLY) },
    ];

    const isInvoice = formData.type === 'create_invoice';
    const templates = isInvoice ? invoiceTemplates : readingTemplates;
    const loadingTemplates = isInvoice ? loadingInvoiceTemplates : loadingReadingTemplates;

    const validateForm = () => {
        const newErrors = {};

        if (!formData.type) newErrors.type = t(KEYS.ACTION_FIELD_TYPE_REQUIRED);
        if (!formData.frequency) newErrors.frequency = t(KEYS.ACTION_FIELD_FREQUENCY_REQUIRED);
        if (!formData.nextRunAt) newErrors.nextRunAt = t(KEYS.ACTION_FIELD_NEXT_RUN_REQUIRED);
        if (!formData.templateUid) {
            newErrors.templateUid = isInvoice
                ? t(KEYS.ACTION_FIELD_INVOICE_TEMPLATE_REQUIRED)
                : t(KEYS.ACTION_FIELD_READING_TEMPLATE_REQUIRED);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData((prev) => {
            const updated = { ...prev, [name]: newValue };
            // Reset template selection when type changes
            if (name === 'type') updated.templateUid = '';
            return updated;
        });

        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const config = isInvoice
                ? { invoice_template_uid: formData.templateUid }
                : { reading_template_uid: formData.templateUid };

            const data = {
                type: formData.type,
                frequency: formData.frequency,
                next_run_at: formData.nextRunAt,
                is_active: formData.isActive,
                config,
            };

            await createAction.mutateAsync({ serviceUid, data });

            setFormData(emptyForm());
            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.ACTION_ADD_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData(emptyForm());
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.ACTION_ADD_TITLE)}
            subtitle="Automate invoice or reading creation"
            size="medium"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleSubmit} disabled={isSubmitting || loadingTemplates}>
                        {isSubmitting ? t(KEYS.ACTION_ADD_SUBMITTING) : t(KEYS.ACTION_ADD_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm mb-4">
                    {submitError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Type */}
                <div className="space-y-1">
                    <label htmlFor="type" className="label-base flex items-center gap-1.5">
                        <Tag className="w-4 h-4" />
                        Type <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        className={`input-base ${errors.type ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    >
                        {typeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    {errors.type && <span className="text-xs text-red-500 mt-1">{errors.type}</span>}
                </div>

                {/* Template */}
                <div className="space-y-1">
                    <label htmlFor="templateUid" className="label-base flex items-center gap-1.5">
                        <Tag className="w-4 h-4" />
                        {isInvoice ? 'Invoice Template' : 'Reading Template'} <span className="text-red-500">*</span>
                    </label>
                    {loadingTemplates ? (
                        <div className="text-sm text-text-muted py-2">{t(KEYS.ACTION_LOADING_TEMPLATES)}</div>
                    ) : (
                        <select
                            id="templateUid"
                            name="templateUid"
                            value={formData.templateUid}
                            onChange={handleChange}
                            className={`input-base ${errors.templateUid ? 'border-red-500 focus:ring-red-500' : ''}`}
                            disabled={isSubmitting}
                        >
                            <option value="">{t(KEYS.ACTION_SELECT_TEMPLATE)}</option>
                            {templates.map((tpl) => (
                                <option key={tpl.uid} value={tpl.uid}>{tpl.label}</option>
                            ))}
                        </select>
                    )}
                    {errors.templateUid && <span className="text-xs text-red-500 mt-1">{errors.templateUid}</span>}
                    {!loadingTemplates && templates.length === 0 && (
                        <p className="text-xs text-text-muted mt-1">
                            No {isInvoice ? 'invoice' : 'reading'} templates found. Create one first.
                        </p>
                    )}
                </div>

                {/* Frequency */}
                <div className="space-y-1">
                    <label htmlFor="frequency" className="label-base flex items-center gap-1.5">
                        <RefreshCw className="w-4 h-4" />
                        Frequency <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="frequency"
                        name="frequency"
                        value={formData.frequency}
                        onChange={handleChange}
                        className={`input-base ${errors.frequency ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    >
                        {frequencyOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    {errors.frequency && <span className="text-xs text-red-500 mt-1">{errors.frequency}</span>}
                </div>

                {/* Next Run At */}
                <div className="space-y-1">
                    <label htmlFor="nextRunAt" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Next Run Date <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        id="nextRunAt"
                        name="nextRunAt"
                        value={formData.nextRunAt}
                        onChange={handleChange}
                        className={`input-base ${errors.nextRunAt ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.nextRunAt && <span className="text-xs text-red-500 mt-1">{errors.nextRunAt}</span>}
                </div>

                {/* Is Active */}
                <div className="space-y-1">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleChange}
                            className="w-4 h-4 rounded border-surface-border bg-surface-overlay cursor-pointer transition-colors"
                            disabled={isSubmitting}
                        />
                        <span className="text-sm text-text-primary flex items-center gap-1.5">
                            <ToggleRight className="w-4 h-4 text-text-muted" />
                            {t(KEYS.ACTION_FIELD_IS_ACTIVE)}
                        </span>
                    </label>
                </div>
            </form>
        </Modal>
    );
};

export default AddActionModal;
